import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Person, UserRole, PresenceStatus, PersonRole, Project } from '../types';
import { Button, PresenceBadge, Modal, Input, TextArea, Drawer, StatusBadge } from './UIComponents';
import {
    Plus,
    MoreHorizontal,
    Mail,
    ChevronRight,
    Search,
    Building2,
    User as UserIcon,
    FolderOpen,
    Archive,
    UserMinus,
    Folder,
    Globe
} from 'lucide-react';

interface PeopleViewProps {
    people: Person[];
    role: UserRole;
    projects?: Project[];
    onAddPerson?: (person: Partial<Person>) => void;
    onRemovePerson?: (id: string) => void;
    onSelectProject?: (id: string) => void;
}

export const PeopleView: React.FC<PeopleViewProps> = ({ people, role, projects = [], onAddPerson, onRemovePerson, onSelectProject }) => {
    const isProvider = role === UserRole.PROVIDER;
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState<'all' | 'internal' | 'receiver'>('all');

    // Invite Form State
    const [inviteName, setInviteName] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRole, setInviteRole] = useState<PersonRole>('Receiver');
    const [inviteNote, setInviteNote] = useState('');

    const selectedPerson = people.find(p => p.id === selectedPersonId);

    // Filter & Search Logic
    const filteredPeople = useMemo(() => {
        return people.filter(p => {
            const matchesSearch =
                p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                p.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (p.company && p.company.toLowerCase().includes(searchQuery.toLowerCase()));

            if (!matchesSearch) return false;

            if (filterType === 'internal') return p.role === 'Provider';
            if (filterType === 'receiver') return p.role === 'Receiver';
            return true;
        });
    }, [people, searchQuery, filterType]);

    // Group by Company
    const groupedPeople = useMemo(() => {
        const groups: Record<string, Person[]> = {
            'Internal Team': [], // Provider members always go here
        };

        filteredPeople.forEach(p => {
            if (p.role === 'Provider') {
                groups['Internal Team'].push(p);
            } else {
                const company = p.company || 'Unaffiliated';
                if (!groups[company]) groups[company] = [];
                groups[company].push(p);
            }
        });

        // Remove empty groups (except Internal Team if we want to show it always, but cleaning is better)
        Object.keys(groups).forEach(key => {
            if (groups[key].length === 0) delete groups[key];
        });

        return groups;
    }, [filteredPeople]);

    // Filter projects shared with the selected person
    const sharedProjects = selectedPerson ? projects.filter(project => {
        if (selectedPerson.role === 'Receiver') {
            return project.receiverEmail === selectedPerson.email;
        }
        if (selectedPerson.role === 'Provider' || selectedPerson.role === 'Collaborator') {
            return project.milestones.some(m => m.assigneeId === selectedPerson.id);
        }
        return false;
    }) : [];

    const handleInvite = () => {
        if (onAddPerson && inviteName && inviteEmail) {
            onAddPerson({
                name: inviteName,
                email: inviteEmail,
                role: inviteRole,
                relationshipStatus: 'Invited',
                initials: inviteName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase(),
                avatarColor: 'bg-zinc-800',
                company: inviteRole === 'Provider' ? 'Provider' : ''
            });
            setIsInviteModalOpen(false);
            setInviteName('');
            setInviteEmail('');
            setInviteNote('');
            setInviteRole('Receiver');
        }
    };

    const RoleBadge = ({ role }: { role: PersonRole }) => {
        let styles = 'bg-zinc-100 text-zinc-600 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:border-zinc-700';
        if (role === 'Provider') styles = 'bg-indigo-50 text-indigo-600 border-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-400 dark:border-indigo-500/20';
        if (role === 'Receiver') styles = 'bg-emerald-50 text-emerald-600 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20';

        return (
            <span className={`px-2.5 py-0.5 rounded-md text-[11px] font-medium border uppercase tracking-wider ${styles}`}>
                {role}
            </span>
        );
    };

    const PersonCard: React.FC<{ person: Person }> = ({ person }) => {
        const [isMenuOpen, setIsMenuOpen] = useState(false);
        const buttonRef = useRef<HTMLButtonElement>(null);
        const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
        const [isHovered, setIsHovered] = useState(false);

        useEffect(() => {
            if (isMenuOpen) {
                const handleScroll = () => setIsMenuOpen(false);
                window.addEventListener('scroll', handleScroll, true);
                window.addEventListener('resize', handleScroll);
                return () => {
                    window.removeEventListener('scroll', handleScroll, true);
                    window.removeEventListener('resize', handleScroll);
                };
            }
        }, [isMenuOpen]);

        const handleMenuClick = (e: React.MouseEvent) => {
            e.stopPropagation();
            if (!isMenuOpen && buttonRef.current) {
                const rect = buttonRef.current.getBoundingClientRect();
                setMenuPos({
                    top: rect.bottom + 4,
                    left: rect.right - 160
                });
            }
            setIsMenuOpen(!isMenuOpen);
        };

        const handleAction = (action: string) => {
            setIsMenuOpen(false);
            if (action === 'remove' && onRemovePerson) onRemovePerson(person.id);
            if (action === 'view') setSelectedPersonId(person.id);
        };

        return (
            <div
                onClick={() => setSelectedPersonId(person.id)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className="group relative flex flex-col bg-white dark:bg-zinc-900/40 border border-zinc-200 dark:border-zinc-800/60 rounded-xl p-5 hover:border-zinc-300 dark:hover:border-zinc-700 hover:shadow-lg dark:hover:shadow-zinc-900/20 transition-all duration-200 cursor-pointer h-[200px]"
            >
                {/* Header: Avatar + Action */}
                <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-sm font-semibold text-white shadow-inner ${person.avatarColor} ring-2 ring-white dark:ring-zinc-900 group-hover:scale-105 transition-transform duration-200`}>
                            {person.initials}
                        </div>
                        <div className="flex flex-col">
                            <h3 className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100 truncate leading-tight group-hover:text-indigo-500 dark:group-hover:text-indigo-400 transition-colors">
                                {person.name}
                            </h3>
                            {person.presence && (
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                                    <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-500">Online</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {isProvider && (
                        <div className="relative">
                            <button
                                ref={buttonRef}
                                onClick={handleMenuClick}
                                className={`p-1.5 rounded-lg transition-all duration-200 ${isMenuOpen || isHovered ? 'opacity-100 text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-zinc-100 dark:hover:bg-zinc-800' : 'opacity-0 text-zinc-400'}`}
                            >
                                <MoreHorizontal size={18} />
                            </button>
                            {isMenuOpen && createPortal(
                                <div className="fixed inset-0 z-50 flex flex-col" style={{ pointerEvents: 'auto' }}>
                                    <div className="fixed inset-0 bg-transparent" onClick={(e) => { e.stopPropagation(); setIsMenuOpen(false); }} />
                                    <div
                                        className="fixed z-50 w-44 bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-lg shadow-xl py-1 overflow-hidden animate-fade-in"
                                        style={{ top: menuPos.top, left: menuPos.left }}
                                    >
                                        <button onClick={(e) => { e.stopPropagation(); handleAction('view'); }} className="w-full text-left px-3 py-2.5 text-[13px] text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-900 flex items-center gap-2.5">
                                            <FolderOpen size={14} /> View Details
                                        </button>
                                        <div className="h-px bg-zinc-100 dark:bg-zinc-900 my-1"></div>
                                        <button onClick={(e) => { e.stopPropagation(); handleAction('remove'); }} className="w-full text-left px-3 py-2.5 text-[13px] text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 flex items-center gap-2.5">
                                            <UserMinus size={14} /> Remove Person
                                        </button>
                                    </div>
                                </div>,
                                document.body
                            )}
                        </div>
                    )}
                </div>

                {/* Body Content */}
                <div className="flex-1 flex flex-col justify-center mb-2">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="text-[13px] text-zinc-500 dark:text-zinc-400 truncate flex items-center gap-1.5">
                            <Mail size={12} className="shrink-0 opacity-70" />
                            {person.email}
                        </div>
                    </div>
                    <div>
                        <RoleBadge role={person.role} />
                    </div>
                </div>

                {/* Footer Metadata */}
                <div className="mt-auto pt-3 border-t border-zinc-100 dark:border-zinc-800 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[12px] font-medium text-zinc-500 dark:text-zinc-400 group-hover:text-zinc-800 dark:group-hover:text-zinc-300 transition-colors">
                        <Folder size={14} className="opacity-70" />
                        <span>{person.sharedProjectsCount || 0} Active Projects</span>
                    </div>
                    {person.relationshipStatus === 'Invited' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-500">Pending</span>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="w-full h-full flex flex-col bg-zinc-50 dark:bg-black animate-fade-in overflow-hidden">
            {/* Toolbar */}
            <div className="px-6 py-6 md:py-8 shrink-0 bg-zinc-50 dark:bg-black border-b border-zinc-200 dark:border-zinc-900/50 z-10">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 max-w-7xl mx-auto w-full">
                    <div>
                        <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mb-1 tracking-tight">
                            {isProvider ? 'People' : 'My Network'}
                        </h1>
                        <p className="text-zinc-500 text-[13px]">
                            Manage your internal team and receiver relationships.
                        </p>
                    </div>

                    <div className="flex items-center gap-3 w-full md:w-auto">
                        <div className="relative flex-1 md:w-64">
                            <Search size={14} className="absolute left-3 top-2.5 text-zinc-400" />
                            <input
                                className="w-full bg-white dark:bg-zinc-900/50 border border-zinc-200 dark:border-zinc-800 rounded-lg pl-9 pr-3 py-2 text-[13px] text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:border-zinc-300 dark:focus:border-zinc-700 transition-colors"
                                placeholder="Search people..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        {isProvider && (
                            <Button onClick={() => setIsInviteModalOpen(true)} variant="primary" size="md" className="shrink-0">
                                <Plus size={16} className="mr-2" /> Invite
                            </Button>
                        )}
                    </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex items-center gap-4 mt-6 max-w-7xl mx-auto">
                    {(['all', 'internal', 'receiver'] as const).map(f => (
                        <button
                            key={f}
                            onClick={() => setFilterType(f)}
                            className={`text-[13px] font-medium pb-2 border-b-2 transition-colors capitalize ${filterType === f
                                ? 'text-zinc-900 dark:text-zinc-100 border-zinc-900 dark:border-zinc-100'
                                : 'text-zinc-500 border-transparent hover:text-zinc-700 dark:hover:text-zinc-300'
                                }`}
                        >
                            {f === 'all' ? 'All People' : f === 'internal' ? 'Internal Team' : 'Receivers'}
                        </button>
                    ))}
                </div>
            </div>

            {/* Scrollable Grid Content */}
            <div className="flex-1 overflow-y-auto px-6 py-8">
                <div className="max-w-7xl mx-auto space-y-10">
                    {Object.keys(groupedPeople).length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-zinc-500">
                            <div className="w-16 h-16 bg-zinc-100 dark:bg-zinc-900 rounded-2xl flex items-center justify-center mb-4">
                                <UserIcon size={32} strokeWidth={1.5} className="text-zinc-300 dark:text-zinc-700" />
                            </div>
                            <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-1">No people found</h3>
                            <p className="text-[13px]">Try adjusting your search or filters.</p>
                        </div>
                    ) : (
                        Object.keys(groupedPeople).map(company => (
                            <div key={company} className="animate-fade-in">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center border ${company === 'Internal Team'
                                        ? 'bg-indigo-50 dark:bg-indigo-500/10 border-indigo-100 dark:border-indigo-500/20 text-indigo-500'
                                        : 'bg-zinc-100 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-800 text-zinc-500'
                                        }`}>
                                        {company === 'Internal Team' ? <Building2 size={16} /> : <Globe size={16} />}
                                    </div>
                                    <h2 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">{company}</h2>
                                    <span className="text-xs text-zinc-500 bg-zinc-100 dark:bg-zinc-900 px-2 py-0.5 rounded-full">
                                        {groupedPeople[company].length}
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                    {groupedPeople[company].map(person => (
                                        <PersonCard key={person.id} person={person} />
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Invite Modal */}
            <Modal isOpen={isInviteModalOpen} onClose={() => setIsInviteModalOpen(false)} title="Invite People">
                <div className="p-6 space-y-5">
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Full Name</label>
                            <Input
                                autoFocus
                                placeholder="e.g. Jane Doe"
                                value={inviteName}
                                onChange={(e) => setInviteName(e.target.value)}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Email Address</label>
                            <Input
                                placeholder="jane@company.com"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Role</label>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => setInviteRole('Receiver')}
                                    className={`text-left p-3 rounded border transition-all ${inviteRole === 'Receiver' ? 'bg-zinc-100 border-zinc-100 text-black' : 'bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-700'}`}
                                >
                                    <div className="font-medium text-[13px] mb-0.5">Receiver</div>
                                    <div className={`text-[11px] ${inviteRole === 'Receiver' ? 'text-zinc-600' : 'text-zinc-600'}`}>Can view progress, approve work.</div>
                                </button>
                                <button
                                    onClick={() => setInviteRole('Collaborator')}
                                    className={`text-left p-3 rounded border transition-all ${inviteRole === 'Collaborator' ? 'bg-zinc-100 border-zinc-100 text-black' : 'bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-700'}`}
                                >
                                    <div className="font-medium text-[13px] mb-0.5">Collaborator</div>
                                    <div className={`text-[11px] ${inviteRole === 'Collaborator' ? 'text-zinc-600' : 'text-zinc-600'}`}>Can view assigned projects.</div>
                                </button>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide">Personal Note (Optional)</label>
                            <TextArea
                                placeholder="Add a personal message..."
                                className="h-20"
                                value={inviteNote}
                                onChange={(e) => setInviteNote(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="pt-2 flex justify-end gap-2">
                        <Button variant="ghost" onClick={() => setIsInviteModalOpen(false)}>Cancel</Button>
                        <Button onClick={handleInvite} disabled={!inviteName || !inviteEmail}>Send Invite</Button>
                    </div>
                </div>
            </Modal>

            {/* Person Context Drawer */}
            <Drawer isOpen={!!selectedPersonId} onClose={() => setSelectedPersonId(null)} title="">
                {selectedPerson && (
                    <div className="flex flex-col h-full animate-fade-in">
                        {/* Header */}
                        <div className="px-6 pb-6 border-b border-zinc-200 dark:border-zinc-900">
                            <div className="flex items-start justify-between mb-4">
                                <div className={`w-16 h-16 rounded-xl flex items-center justify-center text-2xl font-medium text-white border border-zinc-800 ${selectedPerson.avatarColor}`}>
                                    {selectedPerson.initials}
                                </div>
                                <div className="text-right">
                                    {selectedPerson.presence && <PresenceBadge status={selectedPerson.presence} />}
                                </div>
                            </div>
                            <h2 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100 mb-1">{selectedPerson.name}</h2>
                            <div className="text-sm text-zinc-500 mb-3">{selectedPerson.email}</div>
                            <div className="flex items-center gap-2">
                                <RoleBadge role={selectedPerson.role} />
                                {selectedPerson.company && (
                                    <span className="text-[11px] text-zinc-500 border-l border-zinc-800 pl-2 ml-1">
                                        {selectedPerson.company}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Shared Projects */}
                        <div className="flex-1 overflow-y-auto px-6 py-6">
                            <h3 className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide mb-4">Shared Projects ({sharedProjects.length})</h3>

                            {sharedProjects.length === 0 ? (
                                <div className="p-6 bg-zinc-50 dark:bg-zinc-900/30 border border-zinc-200 dark:border-zinc-800 rounded-lg text-center">
                                    <p className="text-[13px] text-zinc-500 mb-1">No shared projects visible.</p>
                                    {isProvider && selectedPerson.role === 'Receiver' && (
                                        <p className="text-[11px] text-zinc-600">Assign this receiver to a project to see it here.</p>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {sharedProjects.map(proj => (
                                        <div
                                            key={proj.id}
                                            onClick={() => { if (onSelectProject) onSelectProject(proj.id); }}
                                            className="group p-4 bg-white dark:bg-zinc-950 border border-zinc-200 dark:border-zinc-800 rounded-lg hover:border-zinc-300 dark:hover:border-zinc-600 transition-all cursor-pointer shadow-sm"
                                        >
                                            <div className="flex items-start justify-between mb-2">
                                                <h4 className="text-[13px] font-medium text-zinc-900 dark:text-zinc-200 group-hover:text-blue-500 transition-colors">
                                                    {proj.name}
                                                </h4>
                                                <ChevronRight size={14} className="text-zinc-400 group-hover:text-blue-500 transition-colors" />
                                            </div>
                                            <div className="flex items-center justify-between">
                                                <StatusBadge status={proj.status} />
                                                <span className="text-[10px] text-zinc-500">
                                                    {selectedPerson.role === 'Receiver' ? 'Receiver Owner' : 'Collaborator'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="mt-8 pt-6 border-t border-zinc-200 dark:border-zinc-900">
                                <h3 className="text-[11px] font-medium text-zinc-500 uppercase tracking-wide mb-2">Relationship Context</h3>
                                <p className="text-[12px] text-zinc-600 dark:text-zinc-400 italic">
                                    {selectedPerson.relationshipStatus === 'Active'
                                        ? `Active since ${new Date().getFullYear()}`
                                        : 'Invite pending'}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </Drawer>
        </div>
    );
};
